import { FC, useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { getContentHeight } from 'components/sections/Navigation/utils';

import useCurrentUser from 'store/currentUser';
import useSport from 'store/sport';

import useScreen from 'hooks/useScreen';

import useDialog from 'store/dialog';
import useWebsiteSports from 'store/websiteSports';
import MobileFooter, { SportNavigation } from '../MobileSportFooter';
import useWindowResize from 'hooks/useWindowResize';
import SectionLoader from 'components/sections/SectionLoader/SectionLoader';

interface SoftSwissProps {
  sportId: string;
}

enum POST_MESSAGES_TYPES {
  page_changed = 'page_changed',
  open_bet_slip = 'open_bet_slip',
}

const SPORT_TAB_QUERY_PARAM = 'tab';
const BET_SLIP_OPEN_QUERY_PARAM = 'betslip_opened';

const LOGIN = 'sportsbook:sign-in';
const REGISTER = 'sportsbook:sign-up';
const DEPOSIT = 'sportsbook:deposit';

const SoftSwiss: FC<SoftSwissProps> = ({ sportId }) => {
  const { isMobile } = useScreen();

  const ref = useRef(null);
  const [sportState] = useSport();
  const [websiteSports] = useWebsiteSports();
  const [userState] = useCurrentUser();
  const [, dialogActions] = useDialog();
  const [isLoaded, setIsLoaded] = useState(false);

  const [height, setHeight] = useState<number>();

  const userExists = !!userState.data.id;
  const [searchParams, setSearchParams] = useSearchParams();

  const activeTab = searchParams.get(SPORT_TAB_QUERY_PARAM) as SportNavigation;
  const isBetSlipOpened = searchParams.get(BET_SLIP_OPEN_QUERY_PARAM) === '1';
  const currentPage = isBetSlipOpened ? SportNavigation.betslip : (activeTab as SportNavigation);

  const postMessage = useCallback((tab: SportNavigation, open_bet_slip: boolean) => {
    if (!ref.current?.contentWindow) return;

    if (tab !== SportNavigation.betslip) {
      ref.current.contentWindow.postMessage(
        {
          type: POST_MESSAGES_TYPES.page_changed,
          payload: { url: SportNavigation.sport === tab ? '/' : `/${tab}` },
        },
        '*'
      );
    } else {
      ref.current.contentWindow.postMessage(
        {
          type: open_bet_slip ? POST_MESSAGES_TYPES.open_bet_slip : POST_MESSAGES_TYPES.page_changed,
          payload: {},
        },
        '*'
      );
    }
  }, []);

  const onChangeSection = (path: SportNavigation) => {
    if (path === SportNavigation.betslip) {
      setSearchParams((prevSearchParams) => {
        if (prevSearchParams.get(BET_SLIP_OPEN_QUERY_PARAM) === '1') {
          prevSearchParams.delete(BET_SLIP_OPEN_QUERY_PARAM);
        } else {
          prevSearchParams.set(BET_SLIP_OPEN_QUERY_PARAM, '1');
        }

        return prevSearchParams;
      });
    } else {
      setSearchParams((prevSearchParams) => {
        prevSearchParams.delete(BET_SLIP_OPEN_QUERY_PARAM);
        prevSearchParams.set(SPORT_TAB_QUERY_PARAM, SportNavigation[path]);
        return prevSearchParams;
      });
    }
  };

  useEffect(() => {
    if (!sportState.data[sportId]) return;

    const handler = (event) => {
      const { hostname } = new URL(sportState.data[sportId]?.url);
      if (event.origin.includes(hostname)) {
        switch (event.data.type) {
          case LOGIN:
            return dialogActions.set('signIn');
          case REGISTER:
            return dialogActions.set('signUp');
          case DEPOSIT:
            return dialogActions.set('deposit');
          default:
            return null;
        }
      }
    };
    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, [sportState.data[sportId]]);

  useEffect(() => {
    if (currentPage && isLoaded) {
      postMessage(currentPage, isBetSlipOpened);
    }
  }, [currentPage, isBetSlipOpened, isLoaded, postMessage]);

  const handleWindowHeight = useCallback(() => {
    setHeight(getContentHeight());
  }, []);

  useWindowResize(handleWindowHeight);

  if (!sportState.data[sportId]) {
    return <SectionLoader />;
  }

  const url = new URL(sportState.data[sportId]?.url);
  url.pathname += websiteSports.softSwissPostFix;

  return (
    <>
      {sportState.data[sportId] && (
        <iframe
          ref={ref}
          key={userExists ? 'user' : 'guest'}
          src={url.toString()}
          onLoad={() => {
            setIsLoaded(true);
            setHeight(getContentHeight());
            currentPage && postMessage(currentPage, isBetSlipOpened);
          }}
          style={{
            width: '100%',
            border: 0,
            height,
          }}
        />
      )}
      {isMobile && (
        <MobileFooter
          currentPage={currentPage}
          onLiveClick={() => onChangeSection(SportNavigation.live)}
          onSportClick={() => onChangeSection(SportNavigation.schedule)}
          onResultsClick={() => onChangeSection(SportNavigation.results)}
          onToggleClick={() => onChangeSection(SportNavigation.betslip)}
        />
      )}
    </>
  );
};

export default SoftSwiss;
